import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  HelpOutline as HelpIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import Layout from "../components/Layout";

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
}

const PRESET_CATEGORIES = [
  "General",
  "League Days",
  "Points & Rankings",
  "Divisions",
  "Playing",
  "Registration",
  "Rules",
  "Schedule",
  "Fees",
];

const CATEGORY_COLORS: Record<string, string> = {
  General: "#1976d2",
  "League Days": "#388e3c",
  "Points & Rankings": "#f57c00",
  Divisions: "#7b1fa2",
  Playing: "#0097a7",
  Registration: "#c62828",
  Rules: "#37474f",
  Schedule: "#00838f",
  Fees: "#558b2f",
};

function colorForCategory(cat: string): string {
  return CATEGORY_COLORS[cat] ?? "#757575";
}

const DEFAULT_FAQ_ITEMS: Array<{ question: string; answer: string; category: string }> = [
  {
    category: "General",
    question: "What is the Stuttgart Padel League (SPL)?",
    answer: "The SPL is a structured padel league running three seasons per year across greater Stuttgart. Inspired by professional football's pyramid system, it features tiered divisions with promotion and relegation each season. Players compete across partner clubs throughout the Stuttgart area.",
  },
  {
    category: "League Days",
    question: "How does a league day work?",
    answer: "A league day is a scheduled event at one of our partner clubs where all players in a division compete against each other. Each player plays multiple matches (typically 4) throughout the day, rotating partners and opponents in a round-robin format. Results are recorded on the day and rankings are updated in real time.",
  },
  {
    category: "League Days",
    question: "How many league days are in a season?",
    answer: "Each season typically runs 10–14 league days held on weekends at our partner clubs across Stuttgart. The exact schedule is published on the public homepage under the 'Upcoming' tab and announced via our official channels.",
  },
  {
    category: "League Days",
    question: "Where are league days played?",
    answer: "League days are held at our partner clubs around greater Stuttgart. The specific venue for each league day is shown in the 'Upcoming' tab on the homepage. We currently have 5 partner clubs with 18 courts between them.",
  },
  {
    category: "Points & Rankings",
    question: "How are ranking points calculated?",
    answer: "Your ranking is calculated using a Bayesian scoring system that balances two factors: your win rate (how often you win your matches) and your participation rate (how many league days you've played). This ensures that regular participants are rewarded alongside players with high win rates. Simply put: show up and win, and your ranking climbs.",
  },
  {
    category: "Points & Rankings",
    question: "What are badges and bonus points?",
    answer: "Badges are achievements you earn during the season for special performances — for example, winning your first match, beating a much higher-ranked player, winning all 4 matches on a league day, or playing in 10 consecutive league days. Each badge carries bonus points that are added to your season total, adding an extra layer of competition beyond match results.",
  },
  {
    category: "Divisions",
    question: "How are the divisions structured?",
    answer: "The SPL runs 8 divisions organized in a pyramid — the Master Division at the top, followed by Divisions 1 through 7. Each division is grouped by skill level. There are separate Open and Women's divisions. Your initial placement is based on your self-rating or imported ranking, and you move up or down based on seasonal results.",
  },
  {
    category: "Divisions",
    question: "How does promotion and relegation work?",
    answer: "After each season, the top 2 players in each division are promoted to the division above, and the bottom 2 players are relegated to the division below. The Master Division (the highest) has no promotion above it. This keeps competition fair and encourages consistent performance throughout the season.",
  },
  {
    category: "Playing",
    question: "Can I play with a partner?",
    answer: "Yes — padel is a doubles sport, so you always play with a partner. You can register with a preferred partner, but partners are not fixed for every match. The format encourages playing with different partners across rounds, which is part of what makes the league dynamic and social.",
  },
  {
    category: "Playing",
    question: "Can I play as a guest?",
    answer: "Guests can participate on individual league days without being registered for the full season, subject to availability and division needs. Guest results are tracked but may be weighted differently in the rankings. Contact the league organizers to arrange a guest spot.",
  },
  {
    category: "Registration",
    question: "What is the season fee?",
    answer: "The seasonal fee is €145, which covers participation in all league days of that season. New players also pay a one-time registration fee when joining for the first time. The fee covers court costs, administration, and the organization of all league day events.",
  },
  {
    category: "Registration",
    question: "How do I register for a season?",
    answer: "Registration opens before the start of each new season. Create your player profile via the 'Register' link, set your skill level, and pay the seasonal fee. If registration is already open, you'll see a banner at the top of the homepage. You can also sign up to be notified when the next registration period opens.",
  },
  {
    category: "General",
    question: "I have more questions — who do I contact?",
    answer: "Reach out via the contact details in the footer of the SPL landing page, or ask your division organizer at the next league day. You can also check the Announcements section on the homepage for the latest news from the league.",
  },
];

const emptyForm = { question: "", answer: "", category: "General" };

const FaqSettings: React.FC = () => {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FaqItem | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<FaqItem | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiRequest("/api/faq");
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load FAQ items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    setSaving(true);
    setError(null);
    try {
      const created: FaqItem[] = [];
      for (const item of DEFAULT_FAQ_ITEMS) {
        const res = await apiRequest("/api/faq", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(item),
        });
        const result = await res.json();
        created.push(result);
      }
      setItems(created);
      setSuccess(`Loaded ${created.length} default questions. Edit them to match your league.`);
    } catch {
      setError("Failed to load template. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item: FaqItem) => {
    setEditingItem(item);
    setForm({ question: item.question, answer: item.answer, category: item.category });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingItem) {
        await apiRequest(`/api/faq/${editingItem.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        setItems((prev) =>
          prev.map((i) =>
            i.id === editingItem.id ? { ...i, ...form } : i,
          ),
        );
        setSuccess("Question updated.");
      } else {
        const res = await apiRequest("/api/faq", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        const created = await res.json();
        setItems((prev) => [...prev, created]);
        setSuccess("Question added.");
      }
      closeDialog();
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`/api/faq/${deleteTarget.id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setSuccess("Question deleted.");
      setDeleteTarget(null);
    } catch {
      setError("Failed to delete. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const moveItem = async (index: number, direction: "up" | "down") => {
    const next = direction === "up" ? index - 1 : index + 1;
    if (next < 0 || next >= items.length) return;

    const reordered = [...items];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    const withNewOrder = reordered.map((item, i) => ({ ...item, order: i + 1 }));
    setItems(withNewOrder);

    try {
      await apiRequest("/api/faq/reorder", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: withNewOrder.map(({ id, order }) => ({ id, order })),
        }),
      });
    } catch {
      setError("Failed to save order. Reloading...");
      load();
    }
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HelpIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight={800}>
                FAQ Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage the questions and answers shown on the public FAQ page.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Preview public FAQ page">
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenIcon />}
                href="/faq"
                target="_blank"
                rel="noopener"
                sx={{ textTransform: "none" }}
              >
                Preview
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Add Question
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 6,
              textAlign: "center",
              borderStyle: "dashed",
              borderRadius: 3,
            }}
          >
            <HelpIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No FAQ items yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              Start from scratch or load the default template with{" "}
              {DEFAULT_FAQ_ITEMS.length} pre-written questions you can then edit.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
                onClick={seedDefaults}
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Load default template
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAdd}
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Add First Question
              </Button>
            </Box>
          </Paper>
        )}

        {!loading && items.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5, ml: 0.5 }}
            >
              {items.length} question{items.length !== 1 ? "s" : ""} · drag arrows to reorder
            </Typography>
            {items.map((item, index) => (
              <Card
                key={item.id}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  borderRadius: 2,
                  borderLeft: `4px solid ${colorForCategory(item.category)}`,
                  transition: "box-shadow 0.15s",
                  "&:hover": { boxShadow: 2 },
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    {/* Reorder arrows */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexShrink: 0,
                        mt: -0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => moveItem(index, "up")}
                        disabled={index === 0}
                        sx={{ p: 0.25 }}
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveItem(index, "down")}
                        disabled={index === items.length - 1}
                        sx={{ p: 0.25 }}
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          {item.question}
                        </Typography>
                        <Chip
                          label={item.category}
                          size="small"
                          sx={{
                            bgcolor: colorForCategory(item.category),
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            height: 20,
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(item)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(item)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {editingItem ? "Edit Question" : "Add Question"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={form.category}
              label="Category"
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            >
              {PRESET_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: colorForCategory(cat),
                        flexShrink: 0,
                      }}
                    />
                    {cat}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Question"
            fullWidth
            multiline
            minRows={2}
            value={form.question}
            onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
            inputProps={{ maxLength: 300 }}
            helperText={`${form.question.length}/300`}
          />

          <TextField
            label="Answer"
            fullWidth
            multiline
            minRows={4}
            value={form.answer}
            onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
            inputProps={{ maxLength: 2000 }}
            helperText={`${form.answer.length}/2000`}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.question.trim() || !form.answer.trim()}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 90 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Delete question?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            "{deleteTarget?.question}"
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 90 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default FaqSettings;
